<template>
  <v-dialog 
    v-model="dialog" 
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-btn
            color="purple"
            class="white--text px-10 mx-1"
            v-on="{ ...on, ...tooltip }"
          >
            Rejoin
          </v-btn>
        </template>
        <span>Continue service year from previous separation</span>
      </v-tooltip>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Rejoin"
      text="Employee Rejoin (continue service from last separation)"
      class="mb-0"
    >
      <form v-if="dialog && !loading">
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="pt-2"
            >
              <v-autocomplete
                v-model="department"
                v-validate="'required'"
                :items="departments"
                :error-messages="errors.collect('department')"    
                data-vv-name="department"
                data-vv-as="department"
                label="Department"
                item-text="name"
                item-value="id"
                outlined
              />
            </v-col>

            <v-col
              v-if="department && filteredSections.length > 0"
              cols="12"
            >
              <v-autocomplete
                v-model="section"
                v-validate="filteredSections && filteredSections.length > 0 ? 'required' : ''"
                :error-messages="errors.collect('section_id')"
                :items="filteredSections"
                :disabled="!department"
                :filled="!department"
                data-vv-name="section_id"
                data-vv-as="section"   
                item-text="name"
                item-value="id"
                value="id"
                label="Section"
                outlined
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="position"
                v-validate="'required'"
                :items="positions"
                :error-messages="errors.collect('position')"    
                data-vv-name="position"
                data-vv-as="position"
                label="Position"
                item-text="label"
                item-value="id"
                outlined
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="superior"
                v-validate="'required'"
                :items="employees"
                :error-messages="errors.collect('superior')"
                data-vv-name="superior"
                data-vv-as="superior"
                item-text="person.name"
                item-value="employee_uid"
                label="New Superior"
                outlined
              />
            </v-col>

            <v-col cols="12">
              <v-menu
                v-model="rejoinDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="rejoinDate"
                    v-validate="'required'"
                    label="Rejoin Date"
                    :error-messages="errors.collect('rejoinDate')"
                    data-vv-name="rejoinDate"  
                    data-vv-as="rejoin date"      
                    readonly
                    class="label"
                    outlined
                    v-on="on"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>
                        mdi-calendar
                      </v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="rejoinDate"
                  no-title
                  :min="rejoinMinDate"
                  :max="today"
                  @input="rejoinDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-container>
      </form> 

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"      
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions> 
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
      rejoinMinDate: {
        type: String, 
        default: '',
      },
    },
    data () {
      return {
        today: new Date().toISOString().slice(0, 10), 
        dialog: false,
        progressing: false,
        loading: false,
        department: null,
        section: null,
        position: null,
        superior: null,
        departments: [],
        sections: [],
        positions: [],
        employees: [],
        rejoinDateMenu: null,
        rejoinDate: null,
      }
    },
    computed: {
      filteredSections () {
        if (!this.department) return
        return this.sections.filter(section => { return section.department_id === this.department })
      },
    },
    watch: {
      dialog: function (val) {
        if (!val) return this.$resetData()

        this.loading = true

        Promise.all([
          this.$api.hrisDepartments().list(['name'], false),  
          this.$api.hrisSections().list(['name'], false),  
          this.$api.positions().list(),
          this.$api.hrisEmployees().list('?with=vessel'),
        ]).then(([department, section, position, superior]) => {
          this.departments = department.data
          this.sections = section.data
          this.positions = position.data.map(pos => ({
            id: pos.id,
            label: `${pos.name} (${pos.job_grade.job_group.name})`,
          }))
          this.employees = _.orderBy(superior.data, ['person.name'], ['asc'])
        })
        
        this.loading = false
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.rejoinEmployees().update(this.employeeUid, this.prepareFormData())
              .then(res => {
                if (res.status >= 200 && res.status < 300) {
                  this.$emit('updated')
                  this.$snack({ message: 'Employee rejoined successfully' })

                  this.dialog = false
                }
              })
              .catch(() => {
                this.$snack({ 
                  message: 'Unable to update',
                  color: 'error',
                }) 
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          department_id: this.department,
          section_id: this.section,
          position_id: this.position,
          superior_id: this.superior,
          rejoin_date: this.rejoinDate,
        }
      },
    },
  }
</script>

<style scoped>
.label >>> .v-label.v-label--active{
  margin-left:-28px;
  background: white;
}
</style>
